import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { userProfileCache } from '../stores/referenceDataCache';

interface WAHAConfig {
  id?: string;
  api_url: string;
  api_key: string;
  session: string;
  phone_number?: string;
}

interface ApiConfig {
  apiUrl: string | null;
  apiKey: string | null;
  wappConfig: WAHAConfig | null;
  isGlobal: boolean | null;
  loading: boolean;
  error: string | null;
}

export function useApiConfig(overrideConfigId?: string | null) {
  const [config, setConfig] = useState<ApiConfig>({
    apiUrl: null,
    apiKey: null,
    wappConfig: null,
    isGlobal: null,
    loading: true,
    error: null
  });

  // Trigger per forzare il refetch della configurazione
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Funzione per forzare il refetch (utile dopo setup)
  const refetch = useCallback(() => {
    console.log('📱 [useApiConfig] Forcing refetch...');
    // Invalida cache
    sessionStorage.clear();
    // Setta loading e triggera refetch
    setConfig(prev => ({ ...prev, loading: true }));
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    // Otteniamo prima l'utente corrente per creare una chiave di cache specifica per questo utente
    const getCurrentUserData = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user?.id) {
          console.log('📱 [useApiConfig] Utente non autenticato, non posso recuperare la configurazione');
          return null;
        }
        
        const orgIdFromCache = await userProfileCache.getOrganizationId(userData.user.id).catch(() => null);

        if (!orgIdFromCache) {
          console.log('📱 [useApiConfig] Utente senza organizzazione, non posso recuperare la configurazione');
          return null;
        }
        
        return { userId: userData.user.id, orgId: orgIdFromCache };
      } catch (e) {
        console.error('📱 [useApiConfig] Errore nel recupero dei dati utente:', e);
        return null;
      }
    };
    
    const loadConfig = async () => {
      // Ottieni l'utente corrente
      const userInfo = await getCurrentUserData();
      if (!userInfo) {
        setConfig(prev => ({
          ...prev,
          loading: false,
          error: 'User not authenticated or missing organization'
        }));
        return;
      }
      
      const { userId, orgId } = userInfo;
      
      // Usa una chiave di cache specifica per questo utente e organizzazione
      const configCacheKey = `whatsapp_config_cache_${userId}_${orgId}`;

      const cachedConfigStr = sessionStorage.getItem(configCacheKey);

      // Try to use cached config first
      if (cachedConfigStr) {
        try {
          const cachedConfig = JSON.parse(cachedConfigStr);
          const cacheTime = cachedConfig.timestamp || 0;
          const now = Date.now();

          // Cache is valid for 15 minutes (ridotto da 30 minuti per aggiornare più frequentemente)
          if (now - cacheTime < 15 * 60 * 1000) {
            // Log solo in dev mode per ridurre verbosity
            if (import.meta.env.DEV) {
              console.log(`📱 [useApiConfig] Cache hit (età: ${Math.round((now - cacheTime)/1000)}s)`);
            }
            setConfig({
              apiUrl: cachedConfig.apiUrl,
              apiKey: cachedConfig.apiKey,
              wappConfig: cachedConfig.wappConfig,
              isGlobal: cachedConfig.isGlobal ?? null,
              loading: false,
              error: null
            });
            return;
          }
        } catch (e) {
          console.error('📱 [useApiConfig] Errore nel parsing della cache:', e);
          // Se c'è un errore nel parsing della cache, proseguiamo con il fetch della config fresca
        }
      }
      
      // Se non c'è cache valida, procediamo con il fetch della configurazione
      try {
        // Usa i dati utente già recuperati invece di richiederli di nuovo
        const { userId, orgId } = userInfo;
        
        // Usiamo gli ID già recuperati per le query successive
        if (!userId) throw new Error('User not authenticated');
        if (!orgId) throw new Error('User not associated with an organization');

        // Determina se dobbiamo cercare configurazione globale o per utente
        // Cerca prima nel settings per verificare la modalità configurata
        let isGlobalWhatsAppConfig = true; // Default: modalità globale
        
        try {
          const { data: settingsData, error: settingsError } = await supabase
            .from('settings')
            .select('whatsapp_global_config')
            .eq('organization_id', orgId)
            .single();
            
          if (!settingsError && settingsData && 'whatsapp_global_config' in settingsData) {
            isGlobalWhatsAppConfig = settingsData.whatsapp_global_config !== false;
          }
          
          console.log(`📱 [useApiConfig] WhatsApp config mode: ${isGlobalWhatsAppConfig ? 'GLOBAL' : 'PER-USER'} for organization ${orgId.substring(0, 8)}...`);
        } catch (settingsError) {
          console.warn('📱 [useApiConfig] Error checking WhatsApp configuration mode, using global mode:', settingsError);
        }
        
        // Se overrideConfigId è passato, fetch diretto per ID (multi-sessione)
        if (overrideConfigId) {
          const { data: directConfig, error: directError } = await supabase
            .from('api_configurations')
            .select('*')
            .eq('id', overrideConfigId)
            .eq('is_active', true)
            .single();

          if (directError || !directConfig) {
            throw new Error(`Config ${overrideConfigId} not found or inactive`);
          }

          const wappConfig: WAHAConfig = {
            id: directConfig.id,
            api_url: directConfig.configuration?.api_url || '',
            api_key: directConfig.api_key || '',
            session: directConfig.configuration?.session || '',
            phone_number: directConfig.configuration?.phone_number || '',
          };

          const newConfig = {
            apiUrl: wappConfig.api_url,
            apiKey: wappConfig.api_key,
            wappConfig,
            isGlobal: !directConfig.user_id,
            loading: false,
            error: null
          };

          sessionStorage.setItem(`whatsapp_config_cache_${userId}_${orgId}`, JSON.stringify({
            ...newConfig,
            timestamp: Date.now()
          }));

          setConfig(newConfig);
          return;
        }

        // Costruisci la query in base alla modalità
        let query = supabase
          .from('api_configurations')
          .select('*')
          .eq('service_name', 'waha')
          .eq('organization_id', orgId)
          .eq('is_active', true); // ✅ Filtra solo config attive

        // Verifica se è disponibile il campo user_id
        const { data: columnCheck, error: columnError } = await supabase
          .from('api_configurations')
          .select('user_id')
          .limit(1);
          
        const hasUserIdColumn = !(columnError && columnError.code === '42703');
        
        // Modifica la query in base alla modalità di configurazione e alla disponibilità della colonna user_id
        if (hasUserIdColumn) {
          if (isGlobalWhatsAppConfig) {
            // Modalità globale: cerca configurazione senza user_id
            query = query.is('user_id', null);
            console.log(`📱 [useApiConfig] Cercando configurazione GLOBALE per l'organizzazione ${orgId.substring(0, 8)}...`);
          } else {
            // Modalità per utente: cerca configurazione per questo utente
            query = query.eq('user_id', userId);
            console.log(`📱 [useApiConfig] Cercando configurazione PER-USER per l'utente ${userId.substring(0, 8)}...`);
          }
        }
        
        // Esegui la query
        let { data, error } = await query.maybeSingle();

        // Tentativo di fallback su configurazione globale se in modalità per-user e nessuna config trovata
        if (!data && !isGlobalWhatsAppConfig && hasUserIdColumn) {
          console.log(`📱 [useApiConfig] Configurazione per-user non trovata, tentativo di fallback su configurazione globale...`);
          
          // Nuova query per cercare configurazione globale
          const fallbackQuery = supabase
            .from('api_configurations')
            .select('*')
            .eq('service_name', 'waha')
            .eq('organization_id', orgId)
            .eq('is_active', true)
            .is('user_id', null)
            .order('created_at', { ascending: false })
            .limit(1);

          const fallbackResult = await fallbackQuery.maybeSingle();

          if (!fallbackResult.error && fallbackResult.data) {
            console.log(`📱 [useApiConfig] Fallback riuscito: trovata configurazione globale`);
            data = fallbackResult.data;
            error = null;  // Reset dell'errore
          } else {
            console.error(`📱 [useApiConfig] Fallback fallito: nessuna configurazione globale disponibile`);
          }
        }

        // Tentativo di fallback su configurazione per-user se in modalità globale e nessuna config trovata
        if (!data && isGlobalWhatsAppConfig && hasUserIdColumn) {
          console.log(`📱 [useApiConfig] Configurazione globale non trovata, tentativo di fallback su configurazione per-user...`);

          // Nuova query per cercare configurazione per-user
          const fallbackQuery = supabase
            .from('api_configurations')
            .select('*')
            .eq('service_name', 'waha')
            .eq('organization_id', orgId)
            .eq('is_active', true)
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(1);

          const fallbackResult = await fallbackQuery.maybeSingle();

          if (!fallbackResult.error && fallbackResult.data) {
            console.log(`📱 [useApiConfig] Fallback riuscito: trovata configurazione per-user`);
            data = fallbackResult.data;
            error = null;  // Reset dell'errore
          } else {
            console.error(`📱 [useApiConfig] Fallback fallito: nessuna configurazione per-user disponibile`);
          }
        }

        // Verifica se ci sono errori o se non è stata trovata nessuna configurazione
        if (error || !data) {
          if (error) {
            console.error(`📱 [useApiConfig] Errore durante la ricerca della configurazione WhatsApp:`, error);
          } else {
            console.error(`📱 [useApiConfig] Nessuna configurazione WhatsApp trovata per questa organizzazione`);
          }
          console.log(`📱 [useApiConfig] Modalità: ${isGlobalWhatsAppConfig ? 'GLOBAL' : 'PER-USER'}, User ID: ${userId.substring(0, 8)}...`);

          // Invalida la cache se presente, per evitare dati stale
          const configCacheKey = `whatsapp_config_cache_${userId}_${orgId}`;
          sessionStorage.removeItem(configCacheKey);
          console.log(`📱 [useApiConfig] Cache invalidata dopo errore configurazione`);

          throw error || new Error('Configurazione WhatsApp non trovata');
        }

        // Verifica che la configurazione sia valida
        if (!data.configuration || typeof data.configuration !== 'object') {
          console.error(`📱 [useApiConfig] Configurazione trovata ma il campo 'configuration' è vuoto o non valido`);
          throw new Error('Invalid WhatsApp configuration format: missing configuration object');
        }

        // Log successo del recupero configurazione
        console.log(`📱 [useApiConfig] Configurazione WhatsApp trovata con successo in modalità ${isGlobalWhatsAppConfig ? 'GLOBAL' : 'PER-USER'}`);
        
        // Verifica la presenza di tutti i campi necessari
        const hasApiUrl = data.configuration.api_url && typeof data.configuration.api_url === 'string';
        const hasApiKey = data.api_key && typeof data.api_key === 'string';
        const hasSession = data.configuration.session && typeof data.configuration.session === 'string';
        
        console.log(`📱 [useApiConfig] Dettagli: API URL ${hasApiUrl ? 'presente' : 'mancante'}, API Key ${hasApiKey ? 'presente' : 'mancante'}, Session ${hasSession ? 'presente' : 'mancante'}`);
        
        // Verifica che tutti i campi necessari siano presenti
        if (!hasApiUrl || !hasApiKey || !hasSession) {
          console.warn(`📱 [useApiConfig] Configurazione incompleta: mancano campi obbligatori`);
        }

        // Salva la configurazione completa di WhatsApp
        const wappConfig = {
          id: data.id,
          api_url: data.configuration.api_url || '',
          api_key: data.api_key || '',
          session: data.configuration.session || '',
          phone_number: data.configuration.phone_number || ''
        };
        
        const newConfig = {
          apiUrl: data.configuration.api_url || null,
          apiKey: data.api_key || null,
          wappConfig: wappConfig,
          isGlobal: !data.user_id,
          loading: false,
          error: null
        };
        
        // Log della configurazione finale
        console.log(`📱 [useApiConfig] Configurazione WhatsApp caricata con successo${!isGlobalWhatsAppConfig ? ' (modalità per-user)' : ' (modalità globale)'}`);
        
        
        // Save to cache with timestamp and user info per organization
        const cacheData = {
          ...newConfig,
          timestamp: Date.now(),
          userId,
          orgId
        };
        sessionStorage.setItem(configCacheKey, JSON.stringify(cacheData));

        // Log solo in dev mode per ridurre verbosity
        if (import.meta.env.DEV) {
          console.log(`📱 [useApiConfig] Config salvata in cache`);
        }
        
        setConfig(newConfig);
      } catch (error) {
        // Ottieni dettagli più specifici sull'errore
        let errorMessage = 'Failed to load API configuration';
        
        if (error instanceof Error) {
          // Per errori di tipo Error, aggiungi il messaggio specifico
          errorMessage = `WhatsApp configuration error: ${error.message}`;
          
          // Log dettagliato dell'errore
          console.error(`📱 [useApiConfig] Errore dettagliato:`, {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
        } else {
          console.error('📱 [useApiConfig] Errore generico durante il caricamento della configurazione:', error);
        }
        
        // Aggiungi contesto all'errore mostrato all'utente
        const userContext = userId ? 
          `per l'utente ${userId.substring(0, 8)}...` : 
          'utente non identificato';
        
        console.error(`📱 [useApiConfig] Impossibile caricare la configurazione WhatsApp ${userContext}`);
        
        setConfig(prev => ({
          ...prev,
          loading: false,
          error: errorMessage
        }));
      }
    };
    
    // Esegui il caricamento della configurazione
    loadConfig();
  }, [refetchTrigger, overrideConfigId]); // Aggiunto refetchTrigger e overrideConfigId

  return { ...config, refetch };
}